import { Link } from "react-router-dom";
import SolutionLayout from "@/pages/solutions/components/SolutionLayout";
import { terminationContent } from "@/mocks/solutions";

export default function WholesaleVoiceTermination() {
  const content = terminationContent;
  return (
    <SolutionLayout
      title="Wholesale Voice Termination"
      tagline="A/Z voice routes from Tier-1 carriers to your switch. Premium and standard tiers, real-time LCR, and verified quality KPIs on every destination."
      badge="Core route · Wholesale Voice Termination"
      breadcrumbLabel="Voice Termination"
    >
      {/* Stats */}
      <section className="pb-10 md:pb-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {content.stats.map((s) => (
              <div key={s.label} className="rounded-xl border border-background-200 bg-background-50 p-5 text-center">
                <div className="text-2xl md:text-3xl font-semibold font-heading text-foreground-950">{s.value}</div>
                <div className="text-xs md:text-sm text-foreground-600 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Route Tiers */}
      <section className="py-14 md:py-20 bg-background-100/60 border-y border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-10">
            <span className="text-xs uppercase tracking-widest text-primary-600 font-semibold">Route tiers</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-foreground-950 leading-tight">
              Four quality tiers. 
            </h2>
            <p className="mt-3 text-foreground-700 max-w-2xl">
              Route selection is configurable per destination — no need to accept one blanket quality level across your entire traffic profile.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {content.tiers.map((t) => {
              const colorClasses: Record<string, { badge: string; border: string; icon: string }> = {
                primary: { badge: "bg-primary-100 text-primary-800", border: "hover:border-primary-400", icon: "text-primary-600" },
                accent: { badge: "bg-accent-100 text-accent-800", border: "hover:border-accent-400", icon: "text-accent-600" },
                secondary: { badge: "bg-secondary-100 text-secondary-800", border: "hover:border-secondary-400", icon: "text-secondary-600" },
                foreground: { badge: "bg-foreground-100 text-foreground-800", border: "hover:border-foreground-400", icon: "text-foreground-600" },
              };
              const cls = colorClasses[t.color] || colorClasses.primary;
              return (
                <div key={t.name} className={`rounded-xl border border-background-200 bg-background-50 p-6 ${cls.border} transition-colors`}>
                  <span className={`inline-block text-[11px] font-medium px-2.5 py-1 rounded-full ${cls.badge}`}>{t.tag}</span>
                  <h3 className="mt-4 text-lg font-semibold text-foreground-950 font-heading">{t.name}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{t.desc}</p>
                  <p className="mt-4 text-xs text-foreground-500">
                    <span className="font-medium text-foreground-700">Best for:</span> {t.bestFor}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Protocols & Codecs */}
      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <span className="text-xs uppercase tracking-widest text-accent-700 font-semibold">Protocols</span>
              <h3 className="mt-3 text-2xl md:text-3xl font-semibold text-foreground-950 leading-tight">Signaling & transport</h3>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {content.protocols.map((p) => (
                  <div key={p.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <div className="flex items-center gap-2">
                      <i className="ri-router-line text-accent-600 w-5 h-5 flex items-center justify-center" />
                      <span className="text-sm font-semibold text-foreground-950">{p.name}</span>
                    </div>
                    <p className="mt-1 text-xs text-foreground-600">{p.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <span className="text-xs uppercase tracking-widest text-primary-600 font-semibold">Codecs</span>
              <h3 className="mt-3 text-2xl md:text-3xl font-semibold text-foreground-950 leading-tight">Audio compression</h3>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {content.codecs.map((c) => (
                  <div key={c.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <div className="flex items-center gap-2">
                      <i className="ri-sound-module-line text-primary-600 w-5 h-5 flex items-center justify-center" />
                      <span className="text-sm font-semibold text-foreground-950">{c.name}</span>
                    </div>
                    <p className="mt-1 text-xs text-foreground-600">{c.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Routing features */}
      <section className="py-14 md:py-20 bg-secondary-950 text-background-100 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 30% 40%, oklch(var(--primary-500) / 0.5), transparent 45%), radial-gradient(circle at 70% 80%, oklch(var(--accent-500) / 0.5), transparent 45%)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <span className="text-xs uppercase tracking-widest text-primary-400 font-semibold">Routing engine</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-semibold leading-tight">
                Route smarter, not harder.
              </h2>
              <p className="mt-4 text-background-300 leading-relaxed">
                Our real-time LCR engine evaluates route quality every 60 seconds and can swap a partner out before your dialer notices the ASR drop. Override rules, margin controls, and per-destination exclusions are all available in the portal and via API.
              </p>
              <Link
                to="/network"
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-accent-400 hover:text-accent-300 cursor-pointer whitespace-nowrap"
              >
                See the full network architecture
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {content.routingFeatures.map((f) => (
                  <div key={f} className="flex items-start gap-3 bg-secondary-900/70 border border-secondary-800 rounded-lg p-4">
                    <i className="ri-checkbox-circle-line text-primary-400 w-5 h-5 flex items-center justify-center shrink-0 mt-0.5" />
                    <span className="text-sm text-background-200 leading-relaxed">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </SolutionLayout>
  );
}